/**
 * Products API - Mudo Network
 * 
 * Functions for querying and managing marketplace products from Supabase.
 * Products include ceramics, tools, materials sold by studios.
 */

import { supabase } from '../../supabaseClient';
import type { Database } from '../database.types';
import { SOLD_GRACE_MS } from './productSold';

type Product = Database['public']['Tables']['products']['Row'];
type ProductInsert = Database['public']['Tables']['products']['Insert'];

/**
 * List marketplace products with filters
 * 
 * @param params - Search parameters
 * @param params.studioId - Filter by specific studio
 * @param params.search - Text search in title and description
 * @param params.minPrice - Minimum price in cents
 * @param params.maxPrice - Maximum price in cents
 * @param params.inStockOnly - Only show products with stock > 0
 * @param params.limit - Max results (default: 50)
 * @returns Array of products with studio information
 * 
 * @example
 * const { data: products } = await listMarketplaceProducts({
 *   search: 'bowl',
 *   inStockOnly: true,
 *   maxPrice: 10000
 * });
 */
export async function listMarketplaceProducts({
  studioId,
  search,
  minPrice,
  maxPrice,
  inStockOnly = false,
  limit = 50,
}: {
  studioId?: string;
  search?: string;
  minPrice?: number;
  maxPrice?: number;
  inStockOnly?: boolean;
  limit?: number;
} = {}) {
  try {
    const isDev = import.meta.env.MODE === 'development';
    const startMs = isDev ? performance.now() : 0;
    
    const soldCutoff = new Date(Date.now() - SOLD_GRACE_MS).toISOString();
    const soldVisibilityFilter = `is_active.eq.true,and(is_active.eq.false,sold_at.gte.${soldCutoff})`;

    let query = supabase
      .from('products')
      // ✅ OPTIMIZATION: Thin list query - exclude heavy fields (description, image_gallery)
      // Detail pages will fetch full records via getProductById()
      .select(
        [
          'id',
          'studio_id',
          'title',
          'price_cents',
          'currency',
          'stock_qty',
          'category',
          'main_image_url',
          'is_active',
          'is_published',
          'created_at',
          'sold_at',
        ].join(',')
      )
      .eq('is_published', true)
      .or(soldVisibilityFilter)
      .order('created_at', { ascending: false })
      .order('id', { ascending: false })
      .limit(limit);

    if (studioId) {
      query = query.eq('studio_id', studioId);
    }

    if (search) {
      // Text search in title and description
      query = query.or(`title.ilike.%${search}%,description.ilike.%${search}%`);
    }

    if (minPrice !== undefined) {
      query = query.gte('price_cents', minPrice);
    }

    if (maxPrice !== undefined) {
      query = query.lte('price_cents', maxPrice);
    }

    if (inStockOnly) {
      query = query.gt('stock_qty', 0);
    }

    const { data: products, error } = await query;

    if (isDev) {
      const elapsedMs = performance.now() - startMs;
      console.log(`[productsApi] listMarketplaceProducts: ${elapsedMs.toFixed(1)}ms`, {
        rowsReturned: products?.length || 0,
        filters: { studioId: !!studioId, search: !!search, minPrice, maxPrice, inStockOnly },
      });
    }

    if (error) throw error;

    // Fetch studio info separately to work around PostgREST schema cache issue
    const studioIds = [...new Set((products || []).map(p => p.studio_id).filter(Boolean))];
    const { data: studios, error: studiosError } = await supabase
      .from('studios')
      .select(
        [
          'id',
          'owner_user_id',
          'name',
          'slug',
          'city',
          'country',
          'hero_image_url',
          'created_at',
        ].join(',')
      )
      .in('id', studioIds);

    if (studiosError) {
      console.warn('Error fetching studio details:', studiosError);
      // Return products without studio info rather than failing
      return { data: products || [], error: null };
    }

    // Map studios to products
    const studioMap = new Map(studios.map(s => [s.id, s]));
    const productsWithStudios = (products || []).map(p => ({
      ...p,
      studio: studioMap.get(p.studio_id) || null,
    }));

    return { data: productsWithStudios, error: null };
  } catch (error) {
    console.error('Error fetching marketplace products:', error);
    return { data: null, error };
  }
}

/**
 * Get a product by ID with studio details
 * 
 * @param id - Product UUID
 * @returns Product object with studio information
 * 
 * @example
 * const { data: product } = await getProductById('product-uuid');
 */
export async function getProductById(id: string) {
  try {
    // ✅ Detail page query - fetch FULL record with all fields
    // List queries are thinned, but detail pages need complete product data
    const { data: product, error } = await supabase
      .from('products')
      .select('*')
      .eq('id', id)
      .eq('is_published', true)
      .eq('is_active', true)
      .single();

    if (error) {
      if (error.code === 'PGRST116') {
        return { data: null, error: null };
      }
      throw error;
    }

    // Fetch studio info separately
    if (product?.studio_id) {
      const { data: studio, error: studioError } = await supabase
        .from('studios')
        .select('*')
        .eq('id', product.studio_id)
        .single();

      if (!studioError && studio) {
        return { data: { ...product, studio }, error: null };
      }
    }

    return { data: product, error: null };
  } catch (error) {
    console.error(`Error fetching product by id "${id}":`, error);
    return { data: null, error };
  }
}

/**
 * List products from a specific studio
 * 
 * @param studioId - Studio UUID
 * @param params - Optional filters
 * @param params.publishedOnly - Only show published products (default: true)
 * @returns Array of products
 * 
 * @example
 * const { data: products } = await listStudioProducts('studio-uuid');
 */
export async function listStudioProducts(
  studioId: string,
  { publishedOnly = true }: { publishedOnly?: boolean } = {}
) {
  try {
    const isDev = import.meta.env.MODE === 'development';
    const startMs = isDev ? performance.now() : 0;
    
    // ✅ OPTIMIZATION: Thin list query - exclude heavy fields
    let query = supabase
      .from('products')
      .select(
        [
          'id',
          'title',
          'price_cents',
          'currency',
          'stock_qty',
          'main_image_url',
          'is_published',
          'is_active',
          'created_at',
        ].join(',')
      )
      .eq('studio_id', studioId)
      .eq('is_active', true)
      .order('created_at', { ascending: false })
      .order('id', { ascending: false });

    if (publishedOnly) {
      query = query.eq('is_published', true);
    }

    const { data, error } = await query;

    if (isDev) {
      const elapsedMs = performance.now() - startMs;
      console.log(`[productsApi] listStudioProducts: ${elapsedMs.toFixed(1)}ms`, {
        studioId: studioId.slice(0, 8) + '...',
        rowsReturned: data?.length || 0,
        publishedOnly,
      });
    }

    if (error) throw error;

    return { data: data || [], error: null };
  } catch (error) {
    console.error('Error fetching studio products:', error);
    return { data: null, error };
  }
}

/**
 * List ALL products for a studio (including unpublished and inactive)
 * For use by studio owners in their management dashboard
 * 
 * @param studioId - Studio UUID
 * @returns Array of all products for this studio
 * 
 * @example
 * const { data: allProducts } = await listAllProductsForStudio('my-studio-id');
 */
export async function listAllProductsForStudio(studioId: string) {
  try {
    const isDev = import.meta.env.MODE === 'development';
    const startMs = isDev ? performance.now() : 0;
    
    // ✅ OPTIMIZATION: Thin list query - exclude heavy fields
    const { data, error } = await supabase
      .from('products')
      .select(
        [
          'id',
          'title',
          'price_cents',
          'currency',
          'stock_qty',
          'main_image_url',
          'is_published',
          'is_active',
          'created_at',
        ].join(',')
      )
      .eq('studio_id', studioId)
      .order('created_at', { ascending: false })
      .order('id', { ascending: false });

    if (isDev) {
      const elapsedMs = performance.now() - startMs;
      console.log(`[productsApi] listAllProductsForStudio: ${elapsedMs.toFixed(1)}ms`, {
        studioId: studioId.slice(0, 8) + '...',
        rowsReturned: data?.length || 0,
      });
    }

    if (error) throw error;

    return { data: data || [], error: null };
  } catch (error) {
    console.error('Error fetching all products for studio:', error);
    return { data: null, error };
  }
}

/**
 * List products for a studio (alias; returns all regardless of publish/active)
 * Matches required API name used by dashboard code.
 *
 * @param studioId - Studio UUID
 * @returns Array of products
 */
export async function listProductsForStudio(studioId: string) {
  return listAllProductsForStudio(studioId);
}

/**
 * Check if a product is in stock
 * 
 * @param productId - Product UUID
 * @returns Stock availability info
 * 
 * @example
 * const { data } = await checkProductStock('product-uuid');
 * // { inStock: true, quantity: 5, unlimited: false }
 */
export async function checkProductStock(productId: string) {
  try {
    const { data, error } = await supabase
      .from('products')
      .select('stock_qty')
      .eq('id', productId)
      .single();

    if (error) throw error;

    const unlimited = data.stock_qty === null;
    const inStock = unlimited || data.stock_qty > 0;

    return {
      data: {
        inStock,
        quantity: data.stock_qty,
        unlimited,
      },
      error: null,
    };
  } catch (error) {
    console.error('Error checking product stock:', error);
    return { data: null, error };
  }
}

/**
 * Create a new product (for authenticated studio owners)
 * 
 * @param product - Product data
 * @returns Created product
 * 
 * @example
 * const { data } = await createProduct({
 *   studio_id: 'studio-uuid',
 *   title: 'Handmade Ceramic Bowl',
 *   description: 'Beautiful glazed bowl...',
 *   price_cents: 8500,
 *   stock_qty: 3
 * });
 */
export async function createProduct(product: ProductInsert) {
  try {
    const { data, error } = await supabase
      .from('products')
      .insert(product)
      .select()
      .single();

    if (error) throw error;

    return { data, error: null };
  } catch (error) {
    console.error('Error creating product:', error);
    return { data: null, error };
  }
}

/**
 * Create a new product for a specific studio
 * Convenience wrapper around createProduct with studio_id set
 * 
 * @param studioId - Studio UUID
 * @param product - Product data (studio_id will be set automatically)
 * @returns Created product
 * 
 * @example
 * const { data } = await createProductForStudio('my-studio-id', {
 *   title: 'Ceramic Mug',
 *   description: 'Handmade stoneware mug',
 *   price_cents: 4500,
 *   stock_qty: 10
 * });
 */
export async function createProductForStudio(
  studioId: string,
  product: Omit<ProductInsert, 'studio_id'>
) {
  return createProduct({
    ...product,
    studio_id: studioId,
  });
}

/**
 * Update a product (for authenticated studio owners)
 * 
 * @param id - Product UUID
 * @param updates - Fields to update
 * @returns Updated product
 * 
 * @example
 * const { data } = await updateProduct('product-uuid', {
 *   price_cents: 9000,
 *   stock_qty: 5,
 *   is_published: true
 * });
 */
export async function updateProduct(
  id: string,
  updates: Database['public']['Tables']['products']['Update']
) {
  try {
    const { data, error } = await supabase
      .from('products')
      .update(updates)
      .eq('id', id)
      .select()
      .single();

    if (error) throw error;

    return { data, error: null };
  } catch (error) {
    console.error('Error updating product:', error);
    return { data: null, error };
  }
}

/**
 * Delete a product (for authenticated studio owners)
 * 
 * @param id - Product UUID
 * @returns Success status
 */
export async function deleteProduct(id: string) {
  try {
    const { error } = await supabase.from('products').delete().eq('id', id);

    if (error) throw error;

    return { data: true, error: null };
  } catch (error) {
    console.error('Error deleting product:', error);
    return { data: false, error };
  }
}

/**
 * Decrement product stock after a purchase
 * This is typically called by backend after payment confirmation
 * 
 * @param productId - Product UUID
 * @param quantity - Amount to decrement
 * @returns Updated product
 */
export async function decrementProductStock(productId: string, quantity: number = 1) {
  try {
    // First fetch current stock
    const { data: product, error: fetchError } = await supabase
      .from('products')
      .select('stock_qty')
      .eq('id', productId)
      .single();

    if (fetchError) throw fetchError;

    // If stock is unlimited (null), don't update
    if (product.stock_qty === null) {
      return { data: product, error: null };
    }

    // Decrement stock
    const newStock = Math.max(0, product.stock_qty - quantity);

    const { data, error } = await supabase
      .from('products')
      .update({ stock_qty: newStock })
      .eq('id', productId)
      .select()
      .single();

    if (error) throw error;

    return { data, error: null };
  } catch (error) {
    console.error('Error decrementing product stock:', error);
    return { data: null, error };
  }
}
